/**
 * PricingTab — contract_type radio (Outright/Differential) với conditional fields.
 * Outright: outright_price, price_unit, fx, vat_rate
 * Differential: ptbf_diffs, futures_month, fixation_call, price_unit, fx, vat_rate
 * Common: fixed_fx, provi_market_rate, stoploss_rate
 */
import {
  Box,
  FormControl,
  FormControlLabel,
  FormLabel,
  MenuItem,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material'
import { Controller, type Control, type FieldErrors } from 'react-hook-form'

import {
  useListFixationCallsQuery,
  useListFuturesMonthsQuery,
  useListPriceUnitsQuery,
} from '@/store/api/lookupsApi'
import type { LocalPFormValues } from './types'

export interface PricingTabProps {
  control: Control<LocalPFormValues>
  errors: FieldErrors<LocalPFormValues>
  contractType: 'Outright' | 'Differential'
}

export function PricingTab({ control, errors, contractType }: PricingTabProps) {
  const { data: priceUnits = [] } = useListPriceUnitsQuery()
  const { data: futuresMonths = [] } = useListFuturesMonthsQuery()
  const { data: fixationCalls = [] } = useListFixationCallsQuery()

  return (
    <Box display="flex" flexDirection="column" gap={2} pt={1}>
      <Controller
        control={control}
        name="contract_type"
        render={({ field }) => (
          <FormControl>
            <FormLabel>Loại hợp đồng *</FormLabel>
            <RadioGroup row {...field}>
              <FormControlLabel value="Outright" control={<Radio />} label="Outright" />
              <FormControlLabel
                value="Differential"
                control={<Radio />}
                label="Differential (PTBF)"
              />
            </RadioGroup>
          </FormControl>
        )}
      />

      {contractType === 'Outright' ? (
        <Box display="flex" gap={2} flexWrap="wrap">
          <Controller
            control={control}
            name="outright_price"
            render={({ field }) => (
              <TextField
                {...field}
                value={field.value === '' ? '' : field.value}
                onChange={(e) =>
                  field.onChange(e.target.value === '' ? '' : Number(e.target.value))
                }
                label="Outright Price *"
                type="number"
                size="small"
                sx={{ flex: '1 1 200px' }}
                error={!!errors.outright_price}
                helperText={errors.outright_price?.message}
              />
            )}
          />
        </Box>
      ) : (
        <Box display="flex" gap={2} flexWrap="wrap">
          <Controller
            control={control}
            name="ptbf_diffs"
            render={({ field }) => (
              <TextField
                {...field}
                value={field.value === '' ? '' : field.value}
                onChange={(e) =>
                  field.onChange(e.target.value === '' ? '' : Number(e.target.value))
                }
                label="PTBF Differential *"
                type="number"
                size="small"
                sx={{ flex: '1 1 200px' }}
                error={!!errors.ptbf_diffs}
                helperText={errors.ptbf_diffs?.message}
              />
            )}
          />
          <Controller
            control={control}
            name="futures_month"
            render={({ field }) => (
              <TextField
                {...field}
                label="Futures Month *"
                select
                size="small"
                sx={{ flex: '1 1 180px' }}
                error={!!errors.futures_month}
                helperText={errors.futures_month?.message}
              >
                <MenuItem value="">— Chưa chọn —</MenuItem>
                {futuresMonths.map((m) => (
                  <MenuItem key={m.value} value={m.value}>
                    {m.label}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
          <Controller
            control={control}
            name="fixation_call"
            render={({ field }) => (
              <TextField
                {...field}
                label="Fixation Call *"
                select
                size="small"
                sx={{ flex: '1 1 180px' }}
                error={!!errors.fixation_call}
                helperText={errors.fixation_call?.message}
              >
                <MenuItem value="">— Chưa chọn —</MenuItem>
                {fixationCalls.map((c) => (
                  <MenuItem key={c.value} value={c.value}>
                    {c.label}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
        </Box>
      )}

      <Box display="flex" gap={2} flexWrap="wrap">
        <Controller
          control={control}
          name="price_unit"
          render={({ field }) => (
            <TextField
              {...field}
              label="Price Unit"
              select
              size="small"
              sx={{ flex: '1 1 180px' }}
              error={!!errors.price_unit}
              helperText={errors.price_unit?.message}
            >
              <MenuItem value="">— Chưa chọn —</MenuItem>
              {priceUnits.map((u) => (
                <MenuItem key={u.value} value={u.value}>
                  {u.label}
                </MenuItem>
              ))}
            </TextField>
          )}
        />
        <Controller
          control={control}
          name="fx"
          render={({ field }) => (
            <TextField
              {...field}
              value={field.value === '' ? '' : field.value}
              onChange={(e) =>
                field.onChange(e.target.value === '' ? '' : Number(e.target.value))
              }
              label="FX"
              type="number"
              size="small"
              sx={{ flex: '1 1 160px' }}
              error={!!errors.fx}
              helperText={errors.fx?.message}
            />
          )}
        />
        <Controller
          control={control}
          name="fixed_fx"
          render={({ field }) => (
            <TextField
              {...field}
              value={field.value === '' ? '' : field.value}
              onChange={(e) =>
                field.onChange(e.target.value === '' ? '' : Number(e.target.value))
              }
              label="Fixed FX"
              type="number"
              size="small"
              sx={{ flex: '1 1 160px' }}
              error={!!errors.fixed_fx}
              helperText={errors.fixed_fx?.message}
            />
          )}
        />
        <Controller
          control={control}
          name="vat_rate"
          render={({ field }) => (
            <TextField
              {...field}
              value={field.value === '' ? '' : field.value}
              onChange={(e) =>
                field.onChange(e.target.value === '' ? '' : Number(e.target.value))
              }
              label="VAT Rate"
              type="number"
              size="small"
              sx={{ flex: '1 1 140px' }}
              error={!!errors.vat_rate}
              helperText={errors.vat_rate?.message}
            />
          )}
        />
      </Box>

      <Typography variant="subtitle2" color="text.secondary">
        Forward / Stop-loss (default: 0.7 / 0.75)
      </Typography>
      <Box display="flex" gap={2} flexWrap="wrap">
        <Controller
          control={control}
          name="provi_market_rate"
          render={({ field }) => (
            <TextField
              {...field}
              value={field.value === '' ? '' : field.value}
              onChange={(e) =>
                field.onChange(e.target.value === '' ? '' : Number(e.target.value))
              }
              label="Provisional Market Rate"
              type="number"
              size="small"
              sx={{ flex: '1 1 200px' }}
              error={!!errors.provi_market_rate}
              helperText={errors.provi_market_rate?.message}
            />
          )}
        />
        <Controller
          control={control}
          name="stoploss_rate"
          render={({ field }) => (
            <TextField
              {...field}
              value={field.value === '' ? '' : field.value}
              onChange={(e) =>
                field.onChange(e.target.value === '' ? '' : Number(e.target.value))
              }
              label="Stop-loss Rate"
              type="number"
              size="small"
              sx={{ flex: '1 1 200px' }}
              error={!!errors.stoploss_rate}
              helperText={errors.stoploss_rate?.message}
            />
          )}
        />
      </Box>
    </Box>
  )
}

export default PricingTab
